import axios from 'axios';
import { queryOptions } from '@tanstack/react-query';
import { ApiException } from '../../types';
import {
  getUser,
  searchUserDirectoryByPartialEmail,
  UserKeys,
} from './users-api';
import { USER_NAME_MAX_LENGTH } from '../utils/username';

export interface UserNameAvailability {
  available: boolean;
  suggestion: string | null;
}

const MAX_CANDIDATE_CONFLICTS = 5;

function isGetUserNotFoundError(error: unknown): boolean {
  if (axios.isAxiosError(error)) {
    const errorResponse = error.response?.data as ApiException | undefined;
    return !!errorResponse?.exceptionType?.endsWith(
      'GoogleJsonResponseException'
    );
  }

  return false;
}

async function userEmailExists(email: string) {
  try {
    await getUser(email);
    return true;
  } catch (error) {
    if (isGetUserNotFoundError(error)) return false;
    throw error;
  }
}

async function getUserNamesOnDomain(userName: string, domain: string) {
  const domainSuffix = `@${domain}`.toLowerCase();
  const users = await searchUserDirectoryByPartialEmail(userName);
  return new Set(
    users
      .flatMap((user) => [
        user.primaryEmail,
        ...(user.aliases ?? []),
        ...(user.nonEditableAliases ?? []),
      ])
      .map((email) => email?.toLowerCase() ?? '')
      .filter((email) => email.endsWith(domainSuffix))
      .map((email) => email.slice(0, -domainSuffix.length))
  );
}

export async function getUserNameAvailability(
  userName: string,
  domain: string
): Promise<UserNameAvailability> {
  if (!(await userEmailExists(`${userName}@${domain}`))) {
    return { available: true, suggestion: userName };
  }

  const takenUserNames = await getUserNamesOnDomain(userName, domain);
  let conflicts = 0;
  for (let suffix = 1; conflicts < MAX_CANDIDATE_CONFLICTS; suffix++) {
    const candidate = `${userName}${suffix}`;
    if (candidate.length > USER_NAME_MAX_LENGTH) break;
    if (takenUserNames.has(candidate)) continue;
    if (!(await userEmailExists(`${candidate}@${domain}`))) {
      return { available: false, suggestion: candidate };
    }
    conflicts++;
  }
  return { available: false, suggestion: null };
}

export function getUserNameAvailabilityQuery(userName: string, domain: string) {
  return queryOptions({
    queryKey: [UserKeys.UserNameAvailability, userName, domain],
    queryFn: () => getUserNameAvailability(userName, domain),
    staleTime: 30_000,
  });
}
